// ./src/components/features/CreateEventWizard/giftRegistry.ts
//
// GIFTS (ROADMAP A2h #140, owner 2026-10-09: for event types where gifts are usual — a baby shower, a wedding, a birthday,
// especially a child's — "the wizard offers to add one or more links to gift registries (the link-buttons feature, #85)
// or to use the signup list for gifts").
//
// Which types those are is the CATALOG's answer (`EventSetupType.gifts`, seeded with the type — migration 2150), never a
// list kept here. The Gifts step then offers both, and the host may take either, both or neither:
//   - REGISTRY LINKS — rows of a name and a web address, saved as ONE ordinary Link Buttons block ("Gift registry") on
//     the event's home page, built by the page editor's own builder, so the editor opens and edits it like any other;
//   - A GIFT LIST — the Sign-up list feature switched on, its step following, its block titled "Gift list".
//
// ⚠ Presence only, as the house rule asks: a row needs a name AND an address; a half-filled row is shown at its own
//   inputs and holds Next — it never vanishes on save (#85's lesson). The address is the host's to write: the live site
//   turns a bare "amazon.com/…" into https (`normalizeLinkUrl`), as it does for every link button.
// ⚠ Pure and TYPE-ONLY in its imports, so `npm run check-event-setup` runs it under Node.
import type { BlockForm } from '@/pages/private/EventPageEditorPage/blockWidgetForm';
import type { WidgetLink } from '@em/blocks';

/** One registry, as the step edits it. */
export interface GiftRegistryLink {
  label: string;
  url: string;
}

/**
 * The Sign-up list as a gift list: `added` — this step switched the feature on (so turning the choice off switches it
 * off again); `existing` — it was already on (turning the choice off only stops calling it a gift list).
 */
export type GiftSignupChoice = 'added' | 'existing';

/** The feature a gift list is (boundary/eventFeature). */
export const SIGNUP_LIST_FEATURE = 'signup_list';

/**
 * Is the event's Sign-up list its GIFT list? Chosen on the Gifts step AND the feature still on — the ONE reading every
 * screen uses (a list switched off on the Features step and back on for snacks later is no gift list).
 */
export const isGiftList = (draft: { giftSignup?: string | null; featuresOn?: readonly string[] | null }): boolean =>
  !!draft.giftSignup && (draft.featuresOn ?? []).includes(SIGNUP_LIST_FEATURE);

/** The icon each registry button carries. */
export const GIFT_LINK_ICON = 'Gift';

/**
 * How a NEW registry block lays its buttons out (owner 2026-10-09): side by side in a row, centred under a centred
 * heading — a few registries read as one short row, not a left-aligned stack. ⓘ A rewrite keeps the block's own settings,
 * so a layout the host changes in the page editor stays theirs.
 */
export const GIFT_LINKS_ALIGN: BlockForm['align'] = 'center';
export const GIFT_LINKS_LAYOUT: BlockForm['buttonLayout'] = 'row';

/** A new, empty row. */
export const emptyGiftLink = (): GiftRegistryLink => ({ label: '', url: '' });

/** Does a row hold anything at all? (An untouched row is ignored; a half-filled one is a problem to show.) */
export const giftLinkStarted = (link: GiftRegistryLink): boolean => !!link.label.trim() || !!link.url.trim();

/** What each STARTED row is missing, keyed by row index — the name, the address, or both. */
export const giftLinkProblems = (links: readonly GiftRegistryLink[]): Record<number, { label: boolean; url: boolean }> => {
  const problems: Record<number, { label: boolean; url: boolean }> = {};

  links.forEach((link, index) => {
    if (!giftLinkStarted(link)) {
      return;
    }

    const label = !link.label.trim();
    const url = !link.url.trim();

    if (label || url) {
      problems[index] = { label, url };
    }
  });

  return problems;
};

/** The rows that will be saved — both halves filled, trimmed, in the host's order. */
export const giftLinksToSave = (links: readonly GiftRegistryLink[]): GiftRegistryLink[] =>
  links.filter((link) => link.label.trim() && link.url.trim()).map((link) => ({ label: link.label.trim(), url: link.url.trim() }));

/**
 * The block's buttons, as the page editor's form holds them: a web address each (#85's `url` kind — the absent kind every
 * button had before), with the gift icon, in the block's own style.
 */
export const giftLinkButtons = (links: readonly GiftRegistryLink[]): BlockForm['links'] =>
  giftLinksToSave(links).map((link) => ({ label: link.label, url: link.url, icon: GIFT_LINK_ICON, variant: 'filled' as const }));

/** The rows the step shows: what the host wrote, else one empty row to start from. */
export const giftLinkRows = (links: readonly GiftRegistryLink[] | null | undefined): GiftRegistryLink[] =>
  links && links.length > 0 ? [...links] : [emptyGiftLink()];

/** A button that is a web address (#85: an absent kind is `url`) — the kind the registry rows are. */
const isUrlButton = (link: { kind?: string | null }): boolean => !link.kind || link.kind === 'url';

/**
 * The registry rows a saved block holds — its web-address buttons, in order. Read back on a revisit, so the step shows
 * what the PAGE holds (a label changed in the page editor since included) rather than what the step last wrote.
 */
export const giftLinksOfBlock = (links: readonly WidgetLink[] | null | undefined): GiftRegistryLink[] =>
  (links ?? []).filter(isUrlButton).map((link) => ({ label: link.label ?? '', url: link.url ?? '' }));

/**
 * The block's buttons after the step rewrites its registries: each web-address button, in place, becomes the next row
 * — keeping the host's own styling of it (icon, colours, variant); rows beyond them are added at the end; web-address
 * buttons beyond the rows are dropped. ⚠ A button to a page, a block or an action (added in the page editor) is not a
 * registry and stays where it is.
 */
export const mergeGiftButtons = (existing: readonly WidgetLink[] | null | undefined, next: readonly WidgetLink[]): WidgetLink[] => {
  const rows = [...next];
  const merged: WidgetLink[] = [];

  for (const link of existing ?? []) {
    if (!isUrlButton(link)) {
      merged.push(link);
      continue;
    }

    const row = rows.shift();

    if (row) {
      merged.push({ ...link, label: row.label, url: row.url });
    }
  }

  return [...merged, ...rows];
};
